<template>
  <div class="maintain-directory-order-component">
    <div id="maintain-directory-order-header">
      <sticky>
        <div class="clearfix header-panel">
          <div class="header">
            <span class="notice-header">{{ $t('directory.maintainOrderTitle') }}</span>
          </div>
          <div class="right-btns">
            <button
              class="btn btn--darkblue"
              :disabled="!allowSave"
              @click="save"
            >{{ $t('action.save') }}</button>
            <button
              class="btn btn--darkblue"
              :disabled="!allowPreview"
              @click="preview"
            >{{ $t('action.preview') }}</button>
            <button
              class="btn btn--orange"
              :disabled="!allowProcess"
              @click="processClickHandler"
            >{{ $t('directory.action.processChangeStatus') }}</button>
          </div>
        </div>
      </sticky>
      <div class="directory-order-search-panel row">
        <div class="multi-select-block multi-select-block--building">
          <label>{{ $t('directory.label.building') }}</label>
          <HKLSelect
            :value="searchParams.building"
            :options="buildingOptions"
            :showSearch="true"
            placeholder=""
            @change="updateBuildingInQuery"
          />
        </div>
        <div class="multi-select-block multi-select-block--type">
          <label>{{ $t('directory.label.view') }}</label>
          <HKLSelect
            :value="searchParams.type"
            :options="viewTypes"
            :showSearch="true"
            placeholder=""
            @change="updateTypeInQuery"
          />
        </div>
        <div
          class="multi-select-block multi-select-block--floor"
          v-show="displayFloorSelect"
        >
          <label>{{ $t('directory.label.floor') }}</label>
          <HKLSelect
            :value="searchParams.floor"
            :options="floorList"
            :showSearch="true"
            placeholder=""
            @change="updateFloorInQuery"
          />
        </div>
        <div
          class="multi-select-block multi-select-block--floor"
          v-if="displayLobbySelect"
        >
          <label>{{ $t('directory.label.floor') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.floor"
            @updateValue="val => updateLobbyFloorInQuery(val)"
          />
        </div>
        <div class="multi-select-block multi-select-block--officeName">
          <label>{{ $t('directory.label.officeName') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.engName"
            @updateValue="val => updateOfficeNameInQuery(val)"
          />
        </div>
        <div
          class="multi-select-block multi-select-block--premises"
          v-show="displayFloorSelect"
        >
          <label>{{ $t('directory.label.premises') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.premises"
            @updateValue="val => updatePremisesInQuery(val)"
          />
        </div>
        <div class="multi-select-block multi-select-block--date">
          <label>{{ $t('directory.label.date') }}</label>
          <DatePicker
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="filterParams.date ? new Date(filterParams.date) : null"
            @selected="val => updateDate(val)"
            @cleared="updateDate(null)"
          />
        </div>
        <div class="multi-select-block">
          <label>&nbsp;</label>
          <button
            class="btn btn--darkblue"
            @click="showDirectory"
            :disabled="!canShowDirectory"
          >{{ $t('action.showDirectory') }}</button>
          <Popover
            :text="$t('directory.hint.onlyShowLevel1')"
            position="top-left"
            :width="300"
          />
        </div>
      </div>
      <DirectoryOrderTable
        ref="directoryOrderTable"
        @updateAllowPreview="updateAllowPreview"
        @updateAllowSave="updateAllowSave"
        @updateAllowProcess="updateAllowProcess"
      />
    </div>
    <ActionConfirmModal
      ref="processConfirmModal"
      @action:afterClose="() => isModalActivated = false"
      :confirmTitle="$t('directory.msg.processConfirm.p1', [processItemList.length])"
    >
      <div slot="content">
        <div class="confirm-process-table">
          <CommonTable
            v-if="isModalActivated"
            ref="confirmProcessTable"
            :needCache="false"
            :tableContentLists="processItemList"
            :renderHeaderList="confirmProcessTableHeader"
            :resizeColumnWhenContentChange="false"
            :defaultOrder="false"
            :needFilter="false"
            tableId="confirm-process-table"
          />
        </div>
        <div class="modal-pure-text">{{ $t('directory.msg.processConfirm.p2') }}</div>
      </div>
    </ActionConfirmModal>
    <NoPremisesAtTerminalModal
      ref="noPremisesAtTerminalModal"
      :companies="companiesNoPremisesAtTerminal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import lodash from 'lodash'

import ActionConfirmModal from '@/controls/ActionConfirmModal'
import CommonTable from '@/controls/CommonTable'
import DatePicker from '@/controls/DatePicker'
import DirectoryOrderTable from './DirectoryOrderTable'
import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Popover from '@/controls/Popover'
import Sticky from '@/controls/Sticky'

import dateUtil from '@/utils/dateUtil'
import { DIR_TYPE } from './consts'
import {
  getCompaniesMissingPremisesAtTerminal,
  confirmProcessTableHeader,
} from './directoryOrderTableHelper'
import NoPremisesAtTerminalModal from './NoPremisesAtTerminalModal'
import { generateLobbyExcel, generateFloorExcel } from '@/api/directories/request'
import { utils as formUtils } from '@/plugin/InputChange'

export default {
  name: 'DirectoryOrder',
  components: {
    Sticky,
    DirectoryOrderTable,
    HKLSelect,
    InputField,
    DatePicker,
    CommonTable,
    ActionConfirmModal,
    NoPremisesAtTerminalModal,
    Popover,
  },
  data () {
    return {
      dateUtil,
      items: null,
      searchParams: {
        building: null,
        type: null,
        floor: null,
      },
      searchCompanyParams: {},
      filterParams: {},
      viewTypes: [DIR_TYPE.LOBBY, DIR_TYPE.FLOOR],
      floorList: [],
      displayFloorSelect: false,
      displayLobbySelect: false,
      allowPreview: false,
      allowSave: false,
      allowProcess: false,
      emptyValue: this.$t('emptyValue'),
      dateFormat: 'DD MMM YY',
      processItemList: [],
      confirmProcessTableHeader,
      companiesNoPremisesAtTerminal: [],
      isModalActivated: false,
    }
  },
  methods: {
    async save () {
      await this.$refs.directoryOrderTable.save()
      formUtils.resetDirty()
    },
    async preview () {
      const type = lodash.get(this.searchParams, 'type')
      switch (type) {
        case DIR_TYPE.LOBBY:
          await this.previewLobby()
          break
        case DIR_TYPE.FLOOR:
          await this.previewFloor()
          break
        default:
          break
      }
    },
    async previewLobby () {
      const previewData = lodash.clone(this.$refs.directoryOrderTable.getPreviewData())
      delete previewData.others
      const date = previewData.date
      delete previewData.date
      const screens = lodash.reduce(previewData, (result, screen, key) => {
        const newScreen = lodash.reduce(screen, (rs, colItem, i) => {
          const ref = lodash.map(colItem, (directory) => {
            const company = lodash.get(directory, 'companies.0')
            let ref1 = {
              enName: lodash.get(company, 'engName', this.emptyValue),
              cnName: lodash.get(company, 'chtName', this.emptyValue),
              floor: lodash.get(company, 'floor', this.emptyValue),
            }
            ref1.subCompanies = lodash.map(company.subCompanies, (subCompany) => {
              let ref2 = {
                enName: lodash.get(subCompany, 'engName', this.emptyValue),
                cnName: lodash.get(subCompany, 'chtName', this.emptyValue),
                floor: lodash.get(subCompany, 'floor', this.emptyValue),
              }
              ref2.subDetails = lodash.map(subCompany.subDetails, (subDetail) => {
                return {
                  enName: lodash.get(subDetail, 'engName', this.emptyValue),
                  cnName: lodash.get(subDetail, 'chtName', this.emptyValue),
                  floor: lodash.get(subDetail, 'floor', this.emptyValue),
                }
              })
              return ref2
            })
            return ref1
          })
          rs.push(ref)
          return rs
        }, [])
        result.push(newScreen)
        return result
      }, [])
      await generateLobbyExcel(this.searchParams.building, {
        screens,
        date: dateUtil.format(date, this.dateFormat),
      })
    },
    async previewFloor () {
      const previewData = this.$refs.directoryOrderTable.getPreviewData()
      const date = previewData.date
      delete previewData.date
      const floorData = lodash.orderBy(lodash.clone(lodash.get(previewData, 'floor')), 'order')
      const companies = lodash.reduce(floorData, (companies, companyInfo) => {
        const mainCompany = companyInfo.companies[0]
        const company = {
          enName: mainCompany.engName,
          cnName: mainCompany.chtName,
          direction: companyInfo.position.toLocaleLowerCase(),
          number: mainCompany.premises,
          subCompanies: [],
        }

        lodash.reduce(mainCompany.subCompanies, (subCompanies, subCompany) => {
          const descriptions = []
          subCompany.subDetails.map(detail => {
            descriptions.push({
              enName: detail.engName,
              cnName: detail.chtName,
            })
          })
          subCompanies.push({
            enName: subCompany.engName,
            cnName: subCompany.chtName,
            number: subCompany.premises,
            descriptions,
          })
          return subCompanies
        }, company.subCompanies)
        companies.push(company)
        return companies
      }, [])
      await generateFloorExcel({
        building: this.searchParams.building,
        floor: this.searchParams.floor,
        floorDisplay: {
          header: this.formatFloor(this.searchParams.floor),
          sheet: this.searchParams.floor,
        },
        date: dateUtil.format(date, this.dateFormat),
        companies})
    },
    formatFloor (floorString) {
      return floorString ? floorString.replace(/[0]*(\w*)[F]/gi, (match, p1) => `${p1}/F`) : ''
    },
    async process () {
      await this.$refs.directoryOrderTable.process()
    },
    updateTypeInQuery (type) {
      this.$set(this.searchParams, 'type', type)
      if (type !== DIR_TYPE.FLOOR) {
        this.$delete(this.searchParams, 'floor')
      }
      this.displayFloorSelect = this.searchParams.type === DIR_TYPE.FLOOR && !!this.searchParams.building
      this.displayLobbySelect = this.searchParams.type === DIR_TYPE.LOBBY && !!this.searchParams.building
      if (this.displayFloorSelect) {
        this.updateFloorList()
        if (this.searchCompanyParams.floor) {
          delete this.searchCompanyParams.floor
        }
      }
      if (this.displayLobbySelect && this.searchCompanyParams.premises) {
        delete this.searchCompanyParams.premises
      }
    },
    updateBuildingInQuery (building) {
      this.$set(this.searchParams, 'building', building)
      this.displayFloorSelect = this.searchParams.type === DIR_TYPE.FLOOR && !!this.searchParams.building
      if (this.displayFloorSelect) {
        this.updateFloorList()
      }
    },
    updateFloorInQuery (floor) {
      this.$set(this.searchParams, 'floor', floor)
    },
    updateFloorList () {
      const selectedBuilding = this.currentUser.buildings.find(building => building.code === this.searchParams.building)
      this.floorList = selectedBuilding ? selectedBuilding.floor : []
    },
    updateOfficeNameInQuery (name) {
      this.searchCompanyParams.engName = (name || '').trim()
    },
    updatePremisesInQuery (premises) {
      this.searchCompanyParams.premises = (premises || '').trim()
    },
    updateLobbyFloorInQuery (lobbyFloor) {
      this.searchCompanyParams.floor = (lobbyFloor || '').trim()
    },
    updateDate (val) {
      this.filterParams.date = val
    },
    updateAllowPreview (val) {
      this.allowPreview = val
    },
    updateAllowSave (val) {
      this.allowSave = val
    },
    updateAllowProcess (val) {
      this.allowProcess = val
    },
    checkSearchParams (searchParams) {
      return searchParams.type && searchParams.building && (searchParams.type !== DIR_TYPE.FLOOR || searchParams.floor)
    },
    async showDirectory () {
      await this.$refs.directoryOrderTable.search(this.searchParams, this.filterParams, this.searchCompanyParams)
    },
    processClickHandler () {
      this.processItemList = this.$refs.directoryOrderTable.getProcessItems()
      const valid = this.validatePremisesAtTerminal(this.processItemList)
      if (valid) {
        this.openProcessConfirmModal()
      } else {
        this.openNoPremisesAtTerminalModal()
      }
    },
    validatePremisesAtTerminal (processItemList) {
      if (this.$refs.directoryOrderTable.viewType === DIR_TYPE.FLOOR) return true
      this.companiesNoPremisesAtTerminal = getCompaniesMissingPremisesAtTerminal(processItemList)
      return this.companiesNoPremisesAtTerminal.length === 0
    },
    openProcessConfirmModal () {
      this.$refs.processConfirmModal.open(this.process)
      this.isModalActivated = true
    },
    openNoPremisesAtTerminalModal () {
      this.$refs.noPremisesAtTerminalModal.open()
      this.isModalActivated = true
    },
  },
  async mounted () {
    const routeQuery = this.$router.currentRoute.query
    if (routeQuery.building && this.buildingOptions.includes(routeQuery.building)) {
      this.updateBuildingInQuery(routeQuery.building)
    }
    if (routeQuery.type) {
      this.updateTypeInQuery(routeQuery.type)
    }
    if (routeQuery.type === DIR_TYPE.FLOOR && routeQuery.floor) {
      this.updateFloorInQuery(routeQuery.floor)
    }
    if (routeQuery.premises) {
      this.updatePremisesInQuery(routeQuery.premises)
    }
    if (routeQuery.lobbyFloor) {
      this.updateLobbyFloorInQuery(routeQuery.lobbyFloor)
    }
    if (routeQuery.tenancyId) {
      this.updateTenancyId(routeQuery.tenancyId)
    }
    if (routeQuery.engName) {
      this.updateOfficeNameInQuery(routeQuery.engName)
    }
    if (routeQuery.date) {
      this.updateDate(routeQuery.date)
    }
    if (this.checkSearchParams(this.searchParams)) {
      await this.showDirectory()
    }
  },
  computed: {
    ...mapGetters('user', ['buildings', 'currentUser']),
    canShowDirectory () {
      return this.checkSearchParams(this.searchParams)
    },
    buildingOptions () {
      return [...new Set(this.buildings)]
    },
  },
  watch: {
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
@import '~styles/popup';

.maintain-directory-order-component {
  background-color: #FBFAFC;
}

#maintain-directory-order-header {
  .nav-bar {
    max-width: 100%;
    margin: 0;
  }

  .inline-date-picker-block .vdp-datepicker {
    width: 120px!important;
    display: inline-block;
    min-height: 34px;
    vertical-align: bottom;
    input[type="text"] {
      margin: 0px;
    }
  }

  .premise-header {
    max-width: 600px;
    overflow: hidden;
  }

  .header-panel {
    box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
  }
}

.directory-order-search-panel {
  width: auto;
  padding:15px 0;
  box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
  background-color: #ffffff;
  margin: 15px;
  align-items: center;
  .multi-select-block {
    margin-left: 15px;
    width: 237px;
    label {
      height: 20px;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 9px;
    }
  }
  .multi-select-block--building {
    width: 120px;
  }
  .multi-select-block--type {
    width: 120px;
  }
  .multi-select-block--floor{
    width: 120px;
  }
  .multi-select-block--officeName {
    width: 250px;
  }
  .multi-select-block--premises {
    width: 180px;
  }
  .multi-select-block--date {
    width: 180px;
    & ::v-deep .vdp-datepicker {
      width: auto;
    }
  }
  & ::v-deep .maintain-input-field input {
    margin-bottom: 0px;
      width: 100%;
  }
  & ::v-deep .hkl-select {
    width: 100%;
  }
}
.confirm-process-table {
  margin-top: 20px;
  margin-bottom: 20px;
  & ::v-deep .table-list {
    width: 100% !important;
  }
  & ::v-deep .common-table-header-container {
    min-width: 100% !important;
    max-width: 100% !important;
  }
  & ::v-deep .common-table {
    min-width: 100% !important;
    max-width: 100% !important;
    min-height: unset;
    margin: 0;
    .engName {
      width: 75%;
    }
    .floor {
      width: 25%;
    }
    .mainCompanyEngName,
    .subCompanyEngName {
      width: 50%;
    }
  }
}
</style>
